import { CodiceEtichettaRitiro, EsitoRitiro } from "../../api/ritiri";

/**
 * Ritiri valori — UNICA fonte dei testi di spiegazione: legenda, aiuti in
 * pagina (tooltip) e scheda "Regole" leggono da qui, così non ci sono due
 * versioni che divergono. Riprendono la specifica docs/specs/ritiri-valori.md
 * (§§ 2, 3, 4, 7).
 *
 * Sono spiegazioni, non regole: esiti, etichette, percentuali e date le
 * calcola solo il motore del backend (§ 0.6). Chi cambia una regola nel motore
 * aggiorna anche il testo qui.
 */

export interface VoceTesto {
  titolo: string;
  testo: string;
}

// ----------------------------------------------------------------------------
// Date (§ 3)
// ----------------------------------------------------------------------------

export type SiglaData = "D" | "P1" | "P2" | "E";

/** Definizioni brevi: legenda e "?" accanto alle date. */
export const DATE: Record<SiglaData, VoceTesto> = {
  D: { titolo: "D", testo: "Scadenza della richiesta: la richiesta di ritiro va fatta entro D alle 16:00." },
  P1: { titolo: "P1", testo: "Primo ritiro utile: il primo giorno di ritiro dopo D." },
  P2: { titolo: "P2", testo: "Ritiro successivo: il giorno di ritiro dopo P1, se la richiesta per P1 non parte." },
  E: { titolo: "E1 / E2", testo: "P1 e P2 alle 14:00, l'ora entro cui avviene il 90% dei ritiri: è l'ora a cui si calcolano le previsioni." },
};

// ----------------------------------------------------------------------------
// Barre di riempimento
// ----------------------------------------------------------------------------

export const BARRE = {
  adesso: "Adesso: riempimento attuale, cioè i versamenti arrivati dopo l'ultimo ritiro.",
  aP2: "A P2: previsione a P2 alle 14:00 nello scenario prudente, cioè se la richiesta per P1 non parte. Euro e banconote sono stime (≈).",
  percentuale:
    "La percentuale è la più alta tra pezzi (sulla capienza del sacco) e valore (sul 90% del valore assicurato).",
  colori: [
    { tono: "primary", testo: "Blu: sotto l'85%." },
    { tono: "accent", testo: "Oro: dall'85% al 99%." },
    { tono: "caldo", testo: "Rosso: dal 100% in su." },
  ],
  mancante: "\"—\": limite o previsione mancante.",
} as const;

// ----------------------------------------------------------------------------
// Scenari (§ 4 punto 6)
// ----------------------------------------------------------------------------

export const SCENARI: VoceTesto[] = [
  { titolo: "Scenario prudente", testo: "90° percentile di quanto è stato versato nelle ultime 12 settimane, nelle stesse ore." },
  { titolo: "Scenario medio", testo: "Media delle stesse settimane." },
];

// ----------------------------------------------------------------------------
// Esiti (§ 4.1), nell'ordine di gravità dell'elenco (§ 7)
// ----------------------------------------------------------------------------

export interface TestoEsito {
  esito: EsitoRitiro;
  nome: string;
  /** Cosa significa: la condizione del § 4.1 in chiaro. */
  quando: string;
  /** Cosa fare. */
  cosaFare: string;
}

export const ESITI: TestoEsito[] = [
  {
    esito: "rischio_blocco",
    nome: "Rischio blocco",
    quando:
      "La giacenza attuale ha già raggiunto un limite, anche se la cassaforte è ferma da oltre 45 giorni (spesso è ferma proprio perché piena), oppure nello scenario prudente lo raggiunge prima del ritiro di P1.",
    cosaFare: "Richiedere il ritiro entro D alle 16:00 per P1 e valutare un ritiro straordinario.",
  },
  {
    esito: "richiedi",
    nome: "Richiedi per P1",
    quando: "Senza un ritiro a P1, a P2 raggiungerebbe il limite nello scenario prudente, oppure arriverebbe al 90% del limite nello scenario medio.",
    cosaFare: "Richiedere il ritiro per P1 entro D alle 16:00.",
  },
  {
    esito: "da_ricontrollare",
    nome: "Da ricontrollare",
    quando: "Nello scenario prudente a P2 arriva almeno all'85% del limite.",
    cosaFare: "Non serve ancora richiedere: ricontrollare entro D alle 16:00, con i dati nuovi.",
  },
  {
    esito: "consigliato",
    nome: "Consigliato",
    quando:
      "A P1 saranno passati almeno tanti giorni dall'ultimo ritiro quanti ne dice l'intervallo economico (al massimo 90), con almeno 1.500 € in cassaforte oppure 90 giorni o più.",
    cosaFare: "Richiesta facoltativa: conviene se c'è già un ritiro nella stessa zona o se il contante fermo è molto.",
  },
  {
    esito: "richiesta_inviata",
    nome: "Richiesta inviata",
    quando: "C'è una richiesta inviata o confermata e non è ancora arrivato un ritiro successivo all'invio.",
    cosaFare: "Nessuna nuova richiesta: si attende che il ritiro compaia su AZNext.",
  },
  {
    esito: "ritiro_fisso",
    nome: "Ritiro fisso",
    quando:
      "Il ritiro fisso confermato cade nel giorno di P1 e la cassaforte sarebbe altrimenti Richiedi, Da ricontrollare o Consigliato: nessuna richiesta extra.",
    cosaFare: "Nessuna richiesta extra: P1 è già coperto dal ritiro fisso.",
  },
  { esito: "nessuna_azione", nome: "Nessuna azione", quando: "Nessuna delle condizioni sopra.", cosaFare: "Niente da fare." },
  {
    esito: "nessun_dato_recente",
    nome: "Nessun dato recente",
    quando: "Nessun versamento, oppure l'ultimo è di oltre 45 giorni fa, e la giacenza non ha raggiunto un limite.",
    cosaFare: "Verificare se la cassaforte è ancora in uso; nessuna richiesta necessaria.",
  },
  {
    esito: "esclusa",
    nome: "Esclusa",
    quando: "Esclusa dal calcolo nelle impostazioni della cassaforte, con il motivo indicato.",
    cosaFare: "Niente da fare finché resta esclusa.",
  },
  {
    esito: "dismessa",
    nome: "Dismessa",
    quando:
      "Dismissione già avvenuta su AZNext (flag con data vuota o passata, o data passata), oppure device dismesso o rubato in app. Con una data futura resta attiva, con l'etichetta \"Dismissione programmata\".",
    cosaFare: "Niente da fare: la cassaforte non è più in servizio.",
  },
];

export function testoEsito(esito: EsitoRitiro): TestoEsito {
  return ESITI.find((e) => e.esito === esito)!;
}

export const INTRO_ESITI =
  "Ogni cassaforte ha un solo esito. Il motore li valuta in quest'ordine e prende il primo che vale: Dismessa, Esclusa, Rischio blocco sulla giacenza attuale (anche per una cassaforte ferma), Nessun dato recente, Rischio blocco sulla previsione, Richiesta inviata e poi gli altri. L'elenco invece è ordinato per gravità, come in questa tabella, e a parità di esito per la previsione a P2 dal valore più alto.";

export const NOTA_STORICO =
  "Senza storico utile non c'è previsione: valgono Rischio blocco sulla giacenza attuale, Richiesta inviata e Consigliato (che non usa la previsione). Con meno di 4 settimane la previsione è poco affidabile e compare l'etichetta \"Storico breve\".";

// ----------------------------------------------------------------------------
// Etichette (§ 4.2 e § 2): i chip grigi dell'elenco
// ----------------------------------------------------------------------------

export const ETICHETTE: Record<CodiceEtichettaRitiro, VoceTesto> = {
  dati_vecchi: {
    titolo: "Dati vecchi",
    testo:
      "L'ultimo versamento è arrivato da più tempo del solito (oltre 72 ore, o oltre l'intervallo più lungo abituale degli ultimi 84 giorni): la previsione potrebbe essere bassa.",
  },
  storico_breve: { titolo: "Storico breve", testo: "Meno di 4 settimane di storico utile: previsione poco affidabile." },
  fisso_proposto: {
    titolo: "Fisso?",
    testo:
      "I ritiri sembrano seguire un giorno fisso ogni settimana. Diventa \"Ritiro fisso\" solo se una persona lo conferma nelle impostazioni della cassaforte.",
  },
  fisso_oggi: { titolo: "Fisso di oggi?", testo: "Oggi è il giorno del ritiro fisso (proposto o confermato), ma non risulta ancora un ritiro." },
  sabato: { titolo: "Sabato", testo: "Almeno 2 ritiri di sabato negli ultimi 180 giorni." },
  non_riconciliato: {
    titolo: "Non riconciliato",
    testo: "L'importo dell'ultimo ritiro differisce di almeno 1 € dalla somma dei versamenti tra il penultimo e l'ultimo ritiro.",
  },
  ritiro_non_effettuato: {
    titolo: "Ritiro non effettuato",
    testo:
      "Il giorno di ritiro di una richiesta inviata o confermata è passato, i dati di quel giorno sono completi e il ritiro non c'è. Dal dettaglio si prepara il sollecito alla vigilanza.",
  },
  richiesta_gia_inviata: {
    titolo: "Richiesta già inviata",
    testo: "Rischio blocco con una richiesta inviata o confermata ancora senza ritiro: da valutare un ritiro straordinario.",
  },
  gdo_mancante: {
    titolo: "GDO mancante",
    testo: "Nessun codice GDO sulla relazione attiva, oppure relazioni attive con GDO diversi e nessuna scelta fatta.",
  },
  non_collegata: {
    titolo: "Non collegata all'anagrafica",
    testo: "Il codice AZNext non corrisponde a nessuna matricola dell'anagrafica: la cassaforte si calcola, ma senza GDO e senza trasportatore.",
  },
  anagrafica_aznext_mancante: {
    titolo: "Anagrafica AZNext mancante",
    testo: "La cassaforte compare nelle operazioni ma non nell'anagrafica AZNext: il prossimo import dell'anagrafica completa i dati.",
  },
  limite_mancante: {
    titolo: "Limite mancante",
    testo: "Su AZNext manca la capienza o il valore assicurato: quel limite non viene valutato, senza valori di ripiego.",
  },
  valore_assicurato_diverso: {
    titolo: "Valore assicurato diverso",
    testo: "Il valore assicurato dell'anagrafica è diverso da quello di AZNext: il calcolo usa quello di AZNext.",
  },
  piu_clienti_dati_diversi: {
    titolo: "Più clienti con dati diversi",
    testo:
      "La cassaforte ha più relazioni attive con GDO, trasportatore, valore assicurato, cliente o sede diversi: per la richiesta alla vigilanza va scelta quella da usare.",
  },
  dismissione_programmata: {
    titolo: "Dismissione programmata",
    testo: "AZNext ha una data di dismissione futura: fino ad allora la cassaforte resta attiva e si calcola normalmente.",
  },
};

// ----------------------------------------------------------------------------
// Scheda "Regole": calcolo, calendario, stagionalità, giorni senza ritiro
// ----------------------------------------------------------------------------

export const CALCOLO: VoceTesto[] = [
  {
    titolo: "Dati noti.",
    testo: "Contano solo le operazioni già ricevute dal portale. La giacenza è la somma dei versamenti dopo l'ultimo ritiro, in banconote e in euro.",
  },
  {
    titolo: "Limiti.",
    testo:
      "In banconote la capienza del sacco impostata su AZNext; in euro il 90% del valore assicurato di AZNext. Se uno dei due manca non si usa un valore di ripiego: la cassaforte ha l'etichetta \"Limite mancante\" e quel limite non si valuta. La percentuale è sempre la più alta delle due.",
  },
  {
    titolo: "Previsione.",
    testo:
      "Si parte dal più recente tra l'ultimo versamento ricevuto, l'ultimo ritiro e 7 giorni fa, e si guarda quanto è stato versato, nelle stesse ore, in ciascuna delle ultime 12 settimane, corretto con il fattore stagionale della categoria di attività e con gli eventi attivi. Si calcola solo per le casseforti attive (non Dismesse, Escluse o senza dati recenti).",
  },
  {
    titolo: "Intervallo economico.",
    testo:
      "Ogni quanti giorni conviene ritirare: oltre, il costo del contante fermo (2% annuo, riconosciuto da Evo alla banca) supera quello di un ritiro (27 €). Si calcola dal versato medio degli ultimi 84 giorni.",
  },
  {
    titolo: "Fattore stagionale.",
    testo:
      "Per ogni categoria, quanto si versa in un mese rispetto alla media dei mesi, sui versamenti dal 1° ottobre 2024 alla fine del mese scorso: il mese in corso entra quando è finito, perché con pochi giorni abbasserebbe il suo indice. Il fattore è il rapporto tra l'indice del mese del ritiro e quello medio delle ultime 12 settimane, contando solo quelle il cui mese ha versamenti, tra 0,6 e 1,6. Se la categoria non ha versamenti nel mese del ritiro, o in nessuna delle ultime 12 settimane, il fattore resta neutro (1).",
  },
  {
    titolo: "Eventi.",
    testo:
      "Gli eventi attivi del calendario eventi correggono la previsione: ogni settimana passata si divide per l'effetto medio degli eventi nei suoi giorni, e il risultato si moltiplica per l'effetto medio dei giorni da prevedere. Il limite 0,6–1,6 vale solo per il fattore stagionale, non per l'effetto degli eventi. Senza eventi attivi la previsione è quella di sempre.",
  },
];

export const CALENDARIO = {
  festivi: "Festivi nazionali: 1/1, 6/1, lunedì di Pasqua, 25/4, 1/5, 2/6, 15/8, 1/11, 8/12, 25/12, 26/12. Il sabato non è mai giorno di ritiro.",
  dueCalendari: [
    {
      titolo: "Giorni per le richieste",
      testo:
        "Da lunedì a venerdì, esclusi i festivi. I giorni senza ritiro qui sotto non contano, perché la vigilanza riceve le email anche quando non ritira. Decide solo la scadenza D.",
    },
    {
      titolo: "Giorni di ritiro",
      testo:
        "Da lunedì a venerdì, esclusi i festivi e i giorni senza ritiro validi per il trasportatore della cassaforte. Decide P1 e P2, che quindi possono cambiare da un trasportatore all'altro.",
    },
  ] as VoceTesto[],
  scadenzaD:
    "D è oggi, se è un giorno per le richieste e non sono ancora le 16:00; altrimenti il giorno per le richieste successivo.",
  base:
    "La testata mostra il calendario base (festivi e giorni senza ritiro validi per tutti). Quando il calendario del trasportatore di una cassaforte dà un P1 diverso, l'elenco lo mostra accanto all'esito. Con più trasportatori non ancora scelti vale il calendario che dà l'esito peggiore.",
};

export const INTRO_STAGIONALITA =
  "Indice di ogni mese rispetto alla media della categoria (1,00 = mese nella media), sui mesi già finiti: il mese in corso entra quando è finito. — = nessun versamento della categoria in quel mese: il fattore resta neutro.";

export const NOTA_STAGIONALITA_EVENTI =
  "Con eventi attivi gli indici sono al netto degli eventi: il versato di ogni mese di una cassaforte è diviso per l'effetto medio degli eventi in quel mese, e il calcolo aggiunge l'effetto solo nei giorni dell'evento. Così Natale non conta due volte.";

export const INTRO_CALENDARIO_EVENTI =
  "Periodi dell'anno in cui il versato di alcune categorie cambia: Natale, Ferragosto, la settimana di Pasqua. Per ogni categoria, ed eventualmente per una regione, un moltiplicatore del versato nei giorni dell'evento (2,00 = il doppio, 0,85 = −15%) e un ritardo in giorni, se l'incasso arriva in cassaforte qualche giorno dopo. Gli eventi nascono non attivi.";

export const CALCOLO_CALENDARIO_EVENTI =
  "Solo gli eventi attivi entrano nel calcolo. Nei loro giorni la previsione si moltiplica per l'effetto; le settimane passate che li contengono, e gli indici di stagionalità, se ne depurano. La regione della cassaforte viene dalla provincia AZNext, o se manca da quella della sede: senza una regione riconosciuta valgono solo gli effetti «tutta Italia». Con eventi sovrapposti vale quello della regione, poi il più breve, poi quello che comincia più tardi: mai il prodotto. Il dettaglio della cassaforte dice quali eventi ha applicato.";

export const INTRO_GIORNI_SENZA_RITIRO =
  "Date in cui la vigilanza non ritira: feste patronali, prefestivi dove non ritira, chiusure. Senza trasportatore la data vale per tutte le casseforti; con il trasportatore solo per quelle che serve (il numero accanto al nome). Spostano P1 e P2, non la scadenza D. Non si modificano: si elimina la data e la si reinserisce.";
